import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { 
  BlogList, 
  BlogDetail, 
  BlogCreate, 
  BlogUpdate, 
  BlogSearchCriteria,
} from '../../_models/blogs/blog.model';
import { ApiResponse } from '../../_models/api-response.model';

@Injectable({
  providedIn: 'root'
})
export class BlogService {
  private adminApiUrl = `${environment.apiUrl}/admin/blogs`;
  private publicApiUrl = `${environment.apiUrl}/blogs`;

  constructor(private http: HttpClient) {}

  // Admin Blog Operations

  getAllBlogs(
    criteria: BlogSearchCriteria = {},
    page: number = 0, 
    size: number = 10, 
    sort: string = 'createdAt', 
    direction: string = 'DESC'
  ): Observable<ApiResponse<any>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (criteria.search) {
      params = params.set('search', criteria.search);
    }
    if (criteria.visible !== undefined) {
      params = params.set('visible', criteria.visible.toString());
    }

    return this.http.get<ApiResponse<any>>(this.adminApiUrl, { params });
  }

  getBlogDetails(blogId: number): Observable<ApiResponse<BlogDetail>> {
    return this.http.get<ApiResponse<BlogDetail>>(`${this.adminApiUrl}/${blogId}`);
  }

  makeBlogVisible(blogId: number): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.adminApiUrl}/${blogId}/make-visible`, {});
  }

  makeBlogHidden(blogId: number): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.adminApiUrl}/${blogId}/make-hidden`, {});
  }

  createBlog(blogCreate: BlogCreate, mainFile?: File): Observable<ApiResponse<BlogDetail>> {
    const formData = new FormData();
    
    const blogBlob = new Blob([JSON.stringify(blogCreate)], { 
      type: 'application/json' 
    });
    formData.append('blog', blogBlob);

    if (mainFile) {
      formData.append('mainFile', mainFile);
    }

    return this.http.post<ApiResponse<BlogDetail>>(this.adminApiUrl, formData);
  }

  updateBlog(
    blogId: number, 
    blogUpdate: BlogUpdate, 
    newMainFile?: File
  ): Observable<ApiResponse<BlogDetail>> {
    const formData = new FormData();
    
    const blogBlob = new Blob([JSON.stringify(blogUpdate)], { 
      type: 'application/json' 
    });
    formData.append('blog', blogBlob);

    if (newMainFile) {
      formData.append('newMainFile', newMainFile);
    }

    return this.http.put<ApiResponse<BlogDetail>>(`${this.adminApiUrl}/${blogId}`, formData);
  }

  deleteBlog(blogId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.adminApiUrl}/${blogId}`);
  }

  // Public Blog Operations
  getPublicBlogs(
    page: number = 0, 
    size: number = 10
  ): Observable<ApiResponse<any>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<ApiResponse<any>>(`${this.publicApiUrl}`, { params });
  }

  getPublicBlogDetail(slug: string): Observable<ApiResponse<BlogDetail>> {
    return this.http.get<ApiResponse<BlogDetail>>(`${this.publicApiUrl}/${slug}`);
  }
}